import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell, AreaChart, Area } from 'recharts';
import { Download, TrendingUp, Users, BookOpen, DollarSign, Activity, Calendar, Filter } from 'lucide-react';
import { api } from '../../api'; // Import api
import toast from 'react-hot-toast'; // Import toast

const AdminStats: React.FC = () => {
  const [timeRange, setTimeRange] = useState('month');
  const [selectedMetric, setSelectedMetric] = useState('revenue');
  const [globalStats, setGlobalStats] = useState<any>({
    totalRevenue: 0,
    totalUsers: 0,
    totalCourses: 0,
    activeSessions: 0,
    averageSessionTime: 0,
    conversionRate: 0,
    customerSatisfaction: 0,
    coursesCompleted: 0,
  });
  const [revenueData, setRevenueData] = useState<any[]>([]);
  const [userGrowthData, setUserGrowthData] = useState<any[]>([]);
  const [deviceUsageData, setDeviceUsageData] = useState<any[]>([]);
  const [conversionFunnelData, setConversionFunnelData] = useState<any[]>([]);
  const [courseStatsData, setCourseStatsData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/dashboard');
      const data = response.data;

      setGlobalStats({
        totalRevenue: data.stats.totalRevenue,
        totalUsers: data.stats.totalUsers,
        totalCourses: data.stats.totalCourses,
        quizActive: data.quizStats?.active ?? 0,
        quizAttempts: data.quizStats?.totalAttempts ?? 0,
        quizAvgScore: data.quizStats?.averageScore ?? 0,
        paymentsSucceeded: data.paymentStats?.succeeded ?? 0,
        revenueThisMonth: data.stats.revenueThisMonth ?? 0,
        activeSessions: 0, // Not available from backend yet
        averageSessionTime: 0, // Not available from backend yet
        conversionRate: 0, // Not available from backend yet
        customerSatisfaction: 0, // Not available from backend yet
        coursesCompleted: 0, // Not available from backend yet
      });

      const monthlyRevenue = data.monthlyStats.map((item: any) => ({
        month: new Date(item._id.year, item._id.month - 1).toLocaleString('fr-FR', { month: 'short' }),
        revenue: item.revenue,
      }));
      setRevenueData(monthlyRevenue);

      setUserGrowthData(data.userGrowth || []);
      setDeviceUsageData(data.deviceUsage || []);
      setCourseStatsData(data.courseStats || []);
      setConversionFunnelData(data.conversionFunnel || []);

    } catch (error) {
      console.error('Error loading stats:', error);
      toast.error('Erreur lors du chargement des statistiques');
    } finally {
      setLoading(false);
    }
  };

  const exportData = (format: 'pdf' | 'csv') => {
    if (format !== 'csv') {
      toast.error('Export PDF non disponible pour le moment');
      return;
    }
    const rows: string[][] = [
      ['Métrique', 'Valeur'],
      ['Revenus totaux (€)', String(globalStats.totalRevenue)],
      ['Utilisateurs', String(globalStats.totalUsers)],
      ['Cours', String(globalStats.totalCourses)],
      ['Quiz actifs', String(globalStats.quizActive ?? 0)],
      ['Tentatives de quiz', String(globalStats.quizAttempts ?? 0)],
      ['Score moyen quiz (%)', String(globalStats.quizAvgScore ?? 0)],
      ['Paiements réussis', String(globalStats.paymentsSucceeded ?? 0)],
      ['Revenus ce mois (€)', String(globalStats.revenueThisMonth ?? 0)],
      [],
      ['Mois', 'Revenus (€)'],
      ...revenueData.map((r: any) => [r.month, String(r.revenue)]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(';')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `statistiques-lms-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Export CSV téléchargé');
  };

  const COLORS = ['#1A6542', '#2D8659', '#4FC08A', '#0F482F', '#8FDCB3', '#25D366'];

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-900">Statistiques & Analytics</h1>
          <div className="flex space-x-3">
            <button
              onClick={() => exportData('pdf')}
              className="flex items-center px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
            >
              <Download className="h-4 w-4 mr-2" />
              PDF
            </button>
            <button
              onClick={() => exportData('csv')}
              className="flex items-center px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
            >
              <Download className="h-4 w-4 mr-2" />
              CSV
            </button>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-brand-100 rounded-lg">
                <DollarSign className="h-6 w-6 text-brand-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Revenus totaux</p>
                <p className="text-2xl font-bold text-gray-900">{globalStats.totalRevenue.toLocaleString()}€</p>
                              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-brand-100 rounded-lg">
                <Users className="h-6 w-6 text-brand-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Utilisateurs actifs</p>
                <p className="text-2xl font-bold text-gray-900">{globalStats.totalUsers.toLocaleString()}</p>
                              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-brand-100 rounded-lg">
                <BookOpen className="h-6 w-6 text-brand-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Quiz actifs</p>
                <p className="text-2xl font-bold text-gray-900">{(globalStats.quizActive ?? 0).toLocaleString()}</p>
                              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-brand-100 rounded-lg">
                <TrendingUp className="h-6 w-6 text-brand-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Tentatives de quiz</p>
                <p className="text-2xl font-bold text-gray-900">{(globalStats.quizAttempts ?? 0).toLocaleString()}</p>
                              </div>
            </div>
          </div>
        </div>

        {/* Time Range Selector */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Évolution temporelle</h2>
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            >
              <option value="week">7 derniers jours</option>
              <option value="month">30 derniers jours</option>
              <option value="quarter">3 derniers mois</option>
              <option value="year">12 derniers mois</option>
            </select>
          </div>

          {/* Revenue Chart */}
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={revenueData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip formatter={(value, name) => [`${value}€`, 'Revenus']} />
                <Area type="monotone" dataKey="revenue" stroke="#2D8659" fill="#2D8659" fillOpacity={0.3} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {userGrowthData.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Croissance des utilisateurs</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={userGrowthData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="students" stroke="#1A6542" strokeWidth={2} name="Étudiants" />
                  <Line type="monotone" dataKey="professors" stroke="#2D8659" strokeWidth={2} name="Professeurs" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
          )}{'/*end userGrowth*/'}
          {deviceUsageData.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Utilisation par appareil</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={deviceUsageData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#0F482F"
                    dataKey="value"
                  >
                    {deviceUsageData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
          )}
        </div>

        {courseStatsData.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance des cours</h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={courseStatsData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="enrollments" fill="#1A6542" name="Inscriptions" />
                <Bar dataKey="completion" fill="#2D8659" name="Taux complétion %" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        )}

        {conversionFunnelData.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Entonnoir de conversion</h3>
          <div className="space-y-4">
            {conversionFunnelData.map((stage, index) => (
              <div key={stage.stage} className="relative">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">{stage.stage}</span>
                  <div className="flex items-center space-x-4">
                    <span className="text-sm text-gray-600">{stage.count.toLocaleString()}</span>
                    <span className="text-sm font-medium text-gray-900">{stage.percentage}%</span>
                  </div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className="bg-gradient-to-r from-brand-500 to-brand-500 h-3 rounded-full transition-all duration-300"
                    style={{ width: `${stage.percentage}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>)}
      </div>
    </Layout>
  );
};

export default AdminStats;